'use client'

/* ─────────────────────────────────────────────────────────────────────────
 * About — the sheets laid out beside the mat.
 *
 * Same desk, same board, same light. The section next door asks its question
 * in cut paper on a cutting mat; this page answers it on the same surface, so
 * every rule it obeys is borrowed rather than invented:
 *
 *   • THE GROUND is the self-healing board — the section's own green, its own
 *     ruling (a heavy line every fifth), drawn in CSS rather than sampled so
 *     it stays exact at any width and can run the length of a scrolling page.
 *   • THE PAPER is the sheet colour the section prints on (#F6F1E6), always
 *     rotated a degree or two, always carrying the same hard offset shadow the
 *     CTA wears — one light, up and to the left, for everything on the desk.
 *   • THE TYPE is the section's two faces and no third: Archivo Black for
 *     anything that is cut out or printed, and the mono, uppercase and tracked
 *     at .12–.16em, for everything that is ANNOTATED on the board.
 *   • THE OBJECTS are the section's own drawn props — the tape, the pins, the
 *     clip, the scissors — imported, not redrawn.
 *   • THE MOTION is one curve, cubic-bezier(.16,1,.3,1), the same one the
 *     section's props land on. Nothing here bounces.
 *
 * The photographs are the same graded roll the section sells, pulled from the
 * same base URL, so the two pages cannot drift apart.
 * ───────────────────────────────────────────────────────────────────────── */

import type { CSSProperties, FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import Link from 'next/link'

import { gsap, ScrollTrigger } from '@/lib/animations/gsap-setup'
import { IMG_BASE, MOODS, STUDIO } from '@/components/sections/cutout/data'

import {
    CoffeeRing,
    Paperclip,
    Pushpin,
    Scissors,
    Tape
} from '@/components/sections/cutout/props'

import {
    FACTS,
    NUMBERS,
    PORTRAIT,
    SHEET,
    SIGNATURE,
    STATEMENT,
    STATEMENT_FRAME,
    STEPS
} from './data'

/** The grease-pencil ring the section draws round a chosen mood, reused at
 *  every size. `preserveAspectRatio` is off so one path can circle a chip, a
 *  numeral or a whole photograph without a second drawing. */
const RING_D =
    'M12 26 C12 8 42 4 62 5 C92 6 112 14 110 28 C108 42 78 49 54 47 C28 45 10 38 14 22'

const PencilRing: FC<{ stroke: string; className?: string }> = ({
    stroke,
    className
}): ReactNode => (
    <svg
        className={`ab-ring ${className ?? ''}`}
        viewBox='0 0 120 52'
        preserveAspectRatio='none'
        aria-hidden
    >
        <path
            d={RING_D}
            fill='none'
            stroke={stroke}
            strokeWidth='2.6'
            strokeLinecap='round'
        />
    </svg>
)

/** The hand-drawn arrow the mat uses under its instruction line. */
const PencilArrow: FC = (): ReactNode => (
    <svg className='ab-arrow' viewBox='0 0 90 34' fill='none' aria-hidden>
        <path
            d='M4 9 Q34 30 84 20'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
        />
        <path
            d='M84 20 L70 15 M84 20 L71 27'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
        />
    </svg>
)

interface ShotProps {
    src: string
    alt: string
    className?: string
    /** Above the fold — the portrait cannot wait for a lazy pass, its reveal
     *  is running on the hero's clock. */
    eager?: boolean
}

/** A print on the desk. The veil is a real element rather than a pseudo, so
 *  the reveal can wipe it off the frame under the same clock as everything
 *  else — a photograph that fades in while its neighbours wipe reads as two
 *  different pages. */
const Shot: FC<ShotProps> = ({ src, alt, className, eager }): ReactNode => (
    <div className={`ab-shot ${className ?? ''}`} data-shot>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
            src={IMG_BASE + src}
            alt={alt}
            loading={eager ? 'eager' : 'lazy'}
            draggable={false}
        />
        <span className='ab-veil' aria-hidden />
    </div>
)

export const AboutDesk: FC = (): ReactNode => {
    const rootRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const root = rootRef.current
        if (!root) return

        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches

        const ctx = gsap.context(() => {
            /* HOVER IS HANDED BACK ONLY ONCE A BLOCK HAS LANDED. The cards
             * carry a CSS transition on `transform` for their lift, and GSAP
             * writes `transform` every frame — leave both switched on at once
             * and the reveal is being transitioned as it is tweened, which
             * smears it. The transition is attached by `.is-in`. */
            const settle = (items: ArrayLike<Element>) => {
                Array.from(items).forEach((el) => el.classList.add('is-in'))
            }

            /* REDUCED MOTION GETS THE PAGE, NOT A SLIDESHOW OF IT. Everything
             * is simply put in its finished state and no trigger is created. */
            if (reduce) {
                const all = root.querySelectorAll('[data-reveal], [data-hero]')
                gsap.set(all, { opacity: 1, y: 0 })
                gsap.set('.ab-veil', { scaleX: 0 })
                gsap.set('.ab-shot img', { scale: 1 })
                gsap.set('.ab-ring path', { strokeDashoffset: 0 })
                settle(root.querySelectorAll('[data-reveal]'))
                return
            }

            /* ── the hero lands ────────────────────────────────────────────
             * Pencil first, then the three cut blocks, then the instruction —
             * the same order the mat writes itself in next door. Every tween
             * is a `fromTo` so GSAP inherits each block's own rotation off the
             * stylesheet instead of flattening it to square on the last frame. */
            const tl = gsap.timeline({ defaults: { ease: 'power3.out' } })

            tl.fromTo(
                '[data-hero="label"]',
                { opacity: 0, y: 16 },
                { opacity: 1, y: 0, duration: 0.7 },
                0.1
            )
                .fromTo(
                    '[data-hero="block"]',
                    { opacity: 0, y: 26, scale: 0.94, filter: 'blur(6px)' },
                    {
                        opacity: 1,
                        y: 0,
                        scale: 1,
                        filter: 'blur(0px)',
                        duration: 0.9,
                        stagger: 0.13
                    },
                    0.24
                )
                .fromTo(
                    '[data-hero="note"]',
                    { opacity: 0, y: 16 },
                    { opacity: 1, y: 0, duration: 0.7 },
                    0.72
                )
                .fromTo(
                    '[data-hero="portrait"]',
                    { opacity: 0, y: 34, rotate: 5.4 },
                    { opacity: 1, y: 0, rotate: 2.4, duration: 1 },
                    0.4
                )

            /* the portrait develops as it arrives */
            tl.to('[data-hero="portrait"] .ab-veil', { scaleX: 0, duration: 0.95 }, 0.55)
                .to('[data-hero="portrait"] img', { scale: 1, duration: 1.3 }, 0.55)

            /* ── scroll reveals ────────────────────────────────────────────
             * One rule for the whole page: a block arrives from 28px below,
             * in groups, once, and never leaves again. */
            const groups = gsap.utils.toArray<HTMLElement>('[data-reveal-group]')

            groups.forEach((group) => {
                const items = group.querySelectorAll('[data-reveal]')
                if (!items.length) return

                gsap.fromTo(
                    items,
                    { opacity: 0, y: 28 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.85,
                        ease: 'power3.out',
                        stagger: 0.08,
                        onComplete: () => settle(items),
                        scrollTrigger: {
                            trigger: group,
                            start: 'top 82%',
                            once: true
                        }
                    }
                )
            })

            /* ── the prints develop where they sit ─────────────────────── */
            gsap.utils.toArray<HTMLElement>('[data-shot]').forEach((shot) => {
                if (shot.closest('[data-hero="portrait"]')) return

                gsap.timeline({
                    scrollTrigger: { trigger: shot, start: 'top 88%', once: true }
                })
                    .to(shot.querySelector('.ab-veil'), {
                        scaleX: 0,
                        duration: 0.9,
                        ease: 'power3.inOut'
                    })
                    .to(
                        shot.querySelector('img'),
                        { scale: 1, duration: 1.25, ease: 'power3.out' },
                        0
                    )
            })

            /* ── the grease pencil draws itself on ─────────────────────── */
            gsap.utils.toArray<SVGPathElement>('.ab-ring path').forEach((path) => {
                /* the ring is drawn over the thing it is ringing, so the
                 * trigger is that thing — an absolutely-placed SVG has no
                 * height of its own to measure against the viewport */
                const anchor = path.closest('figure, li') ?? path

                gsap.to(path, {
                    strokeDashoffset: 0,
                    duration: 0.6,
                    ease: 'power2.inOut',
                    scrollTrigger: { trigger: anchor, start: 'top 88%', once: true }
                })
            })
        }, root)

        /* Fonts land after first paint and the display face is wider than the
         * fallback, so every trigger measured before that is measured against
         * the wrong height. */
        document.fonts?.ready.then(() => ScrollTrigger.refresh()).catch(() => {})

        return () => ctx.revert()
    }, [])

    return (
        <div className='ab-root' ref={rootRef}>
            {/* the board's own marks, under everything */}
            <div className='ab-stains' aria-hidden>
                <div className='ab-stain ab-stain--a'>
                    <CoffeeRing uid='ab-ring-a' />
                </div>
                <div className='ab-stain ab-stain--b'>
                    <CoffeeRing uid='ab-ring-b' />
                </div>
            </div>

            {/* ── the sheet head ──────────────────────────────────────── */}
            <header className='ab-hero'>
                <div className='ab-hero-type'>
                    <p className='ab-pencil' data-hero='label'>
                        ABOUT — SHEET 06 / {STUDIO.name}
                    </p>

                    <h1 className='ab-h1'>
                        <span className='ab-cut ab-cut--pencil' data-hero='block'>
                            SO WHO
                        </span>
                        <span className='ab-cut ab-cut--dark' data-hero='block'>
                            IS HOLDING
                        </span>
                        <span className='ab-cut ab-cut--yellow' data-hero='block'>
                            the camera?
                        </span>
                    </h1>

                    <p className='ab-note' data-hero='note'>
                        <span>TWELVE YEARS, FIVE LOOKS, ONE DESK</span>
                        <PencilArrow />
                    </p>

                    <div className='ab-scissors' aria-hidden>
                        <Scissors uid='ab-scissors' />
                    </div>
                </div>

                <figure className='ab-portrait' data-hero='portrait'>
                    <div className='ab-tape ab-tape--tl' aria-hidden>
                        <Tape uid='ab-tape-1' />
                    </div>
                    <div className='ab-tape ab-tape--br' aria-hidden>
                        <Tape uid='ab-tape-2' />
                    </div>

                    <Shot
                        src={PORTRAIT.src}
                        alt='Maren Okonkwo photographing an interior in Rome, lit by one practical lamp'
                        eager
                    />

                    <figcaption>{PORTRAIT.caption}</figcaption>
                </figure>
            </header>

            {/* ── the statement, on paper ─────────────────────────────── */}
            <section className='ab-statement' data-reveal-group aria-labelledby='statement-head'>
                <article className='ab-sheet' data-reveal>
                    <div className='ab-clip' aria-hidden>
                        <Paperclip uid='ab-clip' />
                    </div>

                    <p className='ab-label'>THE LONG VERSION</p>

                    <h2 className='ab-h2' id='statement-head'>
                        A look is a decision, not an accident.
                    </h2>

                    {STATEMENT.map((p, i) => (
                        <p className='ab-body' key={i} data-reveal>
                            {p}
                        </p>
                    ))}

                    <p className='ab-sign' data-reveal>
                        {SIGNATURE}
                    </p>
                </article>

                <figure className='ab-side' data-reveal>
                    <Shot
                        src={STATEMENT_FRAME.src}
                        alt='A low flame in a dim Copenhagen room, photographed on Portra by one window'
                    />
                    <figcaption>{STATEMENT_FRAME.caption}</figcaption>
                </figure>
            </section>

            {/* ── the numbers, printed on the board ───────────────────── */}
            <section className='ab-numbers' data-reveal-group aria-label='The studio in numbers'>
                {NUMBERS.map((n) => (
                    <div className='ab-num' key={n.k} data-reveal>
                        <strong>{n.v}</strong>
                        <span>{n.k}</span>
                    </div>
                ))}
            </section>

            {/* ── six frames off the strip ────────────────────────────── */}
            <section className='ab-sheetwrap' data-reveal-group aria-labelledby='strip-head'>
                <div className='ab-sec-head'>
                    <p className='ab-pencil' data-reveal>
                        CONTACT SHEET — SELECTS
                    </p>
                    <h2 className='ab-h2 ab-h2--onmat' id='strip-head' data-reveal>
                        Six frames I keep coming back to.
                    </h2>
                </div>

                <div className='ab-strip' data-reveal>
                    <div className='ab-sprockets ab-sprockets--top' aria-hidden />

                    <div className='ab-frames'>
                        {SHEET.map((f, i) => (
                            <figure className='ab-frame' key={f.src} data-reveal>
                                <Shot src={f.src} alt={`${f.title}, ${f.place}, ${f.year}`} />

                                {f.select && <PencilRing stroke='#FF5B39' className='ab-ring--frame' />}

                                <figcaption>
                                    <span className='ab-frame-n'>
                                        {String(i + 1).padStart(2, '0')}
                                    </span>
                                    <span className='ab-frame-t'>{f.title}</span>
                                    <span className='ab-frame-p'>
                                        {f.place} · {f.year}
                                    </span>
                                </figcaption>
                            </figure>
                        ))}
                    </div>

                    <div className='ab-sprockets ab-sprockets--bottom' aria-hidden />
                </div>
            </section>

            {/* ── how a job runs ──────────────────────────────────────── */}
            <section className='ab-steps' data-reveal-group aria-labelledby='steps-head'>
                <div className='ab-sec-head'>
                    <p className='ab-pencil' data-reveal>
                        THE ORDER OF OPERATIONS
                    </p>
                    <h2 className='ab-h2 ab-h2--onmat' id='steps-head' data-reveal>
                        Four moments, and what happens in each.
                    </h2>
                </div>

                <ol className='ab-cards'>
                    {STEPS.map((s, i) => (
                        <li
                            className='ab-card'
                            key={s.n}
                            data-reveal
                            style={{ '--rot': `${i % 2 ? 1.1 : -1.3}deg` } as CSSProperties}
                        >
                            <span className='ab-card-n'>
                                {s.n}
                                <PencilRing stroke='#B23A2B' className='ab-ring--num' />
                            </span>
                            <h3>{s.name}</h3>
                            <p>{s.body}</p>
                        </li>
                    ))}
                </ol>
            </section>

            {/* ── the five looks, as they sit in the drawer ───────────── */}
            <section className='ab-stocks' data-reveal-group aria-labelledby='stocks-head'>
                <div className='ab-sec-head'>
                    <p className='ab-pencil' data-reveal>
                        WHAT EACH LOOK IS ACTUALLY MADE OF
                    </p>
                    <h2 className='ab-h2 ab-h2--onmat' id='stocks-head' data-reveal>
                        Five looks. Five stocks. No filters.
                    </h2>
                </div>

                <ul className='ab-stocklist'>
                    {MOODS.map((m) => (
                        <li
                            className='ab-stock'
                            key={m.id}
                            data-reveal
                            style={
                                { '--paper': m.paper, '--ink': m.ink } as CSSProperties
                            }
                        >
                            <span className='ab-chip'>{m.word}</span>
                            <span className='ab-stock-film'>{m.stock}</span>
                            <span className='ab-stock-note'>{m.statement}</span>
                        </li>
                    ))}
                </ul>

                <p className='ab-stocks-foot' data-reveal>
                    <Link href='/'>Go and pick one on the mat →</Link>
                </p>
            </section>

            {/* ── pinned to the board ─────────────────────────────────── */}
            <section className='ab-facts' data-reveal-group aria-labelledby='facts-head'>
                <div className='ab-sec-head'>
                    <p className='ab-pencil' data-reveal>
                        PINNED TO THE BOARD
                    </p>
                    <h2 className='ab-h2 ab-h2--onmat' id='facts-head' data-reveal>
                        The answers people email for.
                    </h2>
                </div>

                <ul className='ab-notes'>
                    {FACTS.map((f, i) => (
                        <li
                            className='ab-noteslip'
                            key={f.k}
                            data-reveal
                            style={{ '--rot': `${(i % 3) - 1}deg` } as CSSProperties}
                        >
                            <span className='ab-pin' aria-hidden>
                                <Pushpin
                                    uid={`ab-pin-${i}`}
                                    hue={i % 2 ? '#CC3A22' : '#E4C33C'}
                                />
                            </span>
                            <strong>{f.k}</strong>
                            <span>{f.v}</span>
                        </li>
                    ))}
                </ul>
            </section>

            {/* ── the last word ───────────────────────────────────────── */}
            <section className='ab-cta' data-reveal-group>
                <div className='ab-ctasheet' data-reveal>
                    <div className='ab-tape ab-tape--top' aria-hidden>
                        <Tape uid='ab-tape-3' tint='#EFB61F' />
                    </div>

                    <h2 className='ab-h2'>Bring me something ordinary.</h2>

                    <p className='ab-body'>
                        Tell me what the photographs are for and I will tell you which of
                        the five looks I would cut it with, before you have paid me anything.
                    </p>

                    <div className='ab-ctarow'>
                        <Link href='/contact' className='ab-btn'>
                            Start a job
                        </Link>
                        <Link href='/' className='ab-mail'>
                            Pick a look
                        </Link>
                        <a href={`mailto:${STUDIO.mail}`} className='ab-mail'>
                            {STUDIO.mail}
                        </a>
                    </div>
                </div>
            </section>

            <footer className='ab-foot'>
                <p>
                    {STUDIO.name} · {STUDIO.role} · {STUDIO.city}
                </p>
                <Link href='/'>← back to the mat</Link>
            </footer>

            <style>{`
                /* ── the board ───────────────────────────────────────────
                 * Drawn, never sampled: the section's own green, its ruling
                 * (a heavy line every fifth) and a mottle over the top,
                 * because a self-healing mat is a pressed sheet and never one
                 * flat colour. It tiles for the length of the page, so the
                 * board has no seam and no resolution to run out of. */
                .ab-root {
                    /* the board's pitch: a rule every centimetre, a heavy one
                     * every fifth — the section's own MAT_MINOR / MAT_MAJOR,
                     * held in variables so a phone can print a finer board
                     * without restating the whole surface */
                    --minor: 34px;
                    --major: 170px;
                    position: relative;
                    min-height: 100vh;
                    overflow: hidden;
                    color: #F4F9EC;
                    background-color: #2C4E3E;
                    background-image:
                        repeating-linear-gradient(to right, rgba(238,244,222,.30) 0 1px, transparent 1px var(--major)),
                        repeating-linear-gradient(to bottom, rgba(238,244,222,.30) 0 1px, transparent 1px var(--major)),
                        repeating-linear-gradient(to right, rgba(233,240,216,.14) 0 1px, transparent 1px var(--minor)),
                        repeating-linear-gradient(to bottom, rgba(233,240,216,.14) 0 1px, transparent 1px var(--minor)),
                        radial-gradient(60% 40% at 18% 12%, rgba(53,96,73,.85), transparent 70%),
                        radial-gradient(50% 35% at 82% 46%, rgba(53,96,73,.7), transparent 72%),
                        radial-gradient(45% 30% at 30% 84%, rgba(41,74,58,.9), transparent 70%);
                    font-family: var(--font-geist-sans), system-ui, sans-serif;
                }
                .ab-root::before {
                    /* blade scars — the one detail that separates a used mat
                     * from a swatch */
                    content: '';
                    position: absolute;
                    inset: 0;
                    pointer-events: none;
                    background-image:
                        linear-gradient(74deg, transparent 49.7%, rgba(232,240,222,.10) 49.7% 49.9%, transparent 49.9%),
                        linear-gradient(-58deg, transparent 31.4%, rgba(232,240,222,.08) 31.4% 31.55%, transparent 31.55%),
                        linear-gradient(21deg, transparent 68%, rgba(232,240,222,.07) 68% 68.15%, transparent 68.15%);
                    background-size: 100% 900px, 100% 1400px, 100% 1100px;
                }
                .ab-stains { position: absolute; inset: 0; pointer-events: none; }
                .ab-stain { position: absolute; opacity: .5; }
                .ab-stain--a { width: 220px; height: 220px; top: 62vh; right: 6vw; }
                .ab-stain--b { width: 160px; height: 160px; top: 210vh; left: 4vw; }

                /* ── shared type ─────────────────────────────────────────
                 * Two faces and no third. Anything ANNOTATED on the board is
                 * mono, uppercase and tracked; anything CUT OUT or PRINTED is
                 * the display face. */
                .ab-pencil, .ab-label, .ab-note, .ab-num span, .ab-frame figcaption,
                .ab-side figcaption, .ab-portrait figcaption, .ab-stock-film,
                .ab-noteslip strong, .ab-foot, .ab-btn, .ab-mail, .ab-stocks-foot {
                    font-family: var(--font-geist-mono), ui-monospace, monospace;
                }
                .ab-h1, .ab-h2, .ab-cut, .ab-card h3, .ab-num strong, .ab-chip {
                    font-family: var(--font-archivo-black), Impact, sans-serif;
                    font-weight: 400;
                }
                .ab-pencil {
                    margin: 0;
                    font-size: clamp(10px, 1.5vw, 12px);
                    letter-spacing: .16em;
                    text-transform: uppercase;
                    color: rgba(244,249,236,.9);
                }
                .ab-label {
                    margin: 0 0 10px;
                    font-size: 10px;
                    letter-spacing: .16em;
                    text-transform: uppercase;
                    opacity: .5;
                }

                /* THE REVEAL CONTRACT. Only opacity is held back in CSS — the
                 * offset and the blur belong to the tween, because every rule
                 * below also owns a ROTATION and a transform written here
                 * would be the one GSAP inherits and then flatten it. */
                [data-reveal], [data-hero] { opacity: 0; }

                .ab-veil {
                    position: absolute;
                    inset: 0;
                    background: #EFE7D6;
                    transform: scaleX(1);
                    transform-origin: 100% 50%;
                    pointer-events: none;
                }

                /* ── prints ──────────────────────────────────────────────
                 * A photograph on this desk always sits on paper: a cream
                 * border, one hard offset shadow, and it lifts under the
                 * cursor the way a print lifts off a mat. */
                .ab-shot {
                    position: relative;
                    overflow: hidden;
                    background: #1B1712;
                    border-radius: 1px;
                }
                .ab-shot img {
                    display: block;
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    transform: scale(1.09);
                    user-select: none;
                    -webkit-user-drag: none;
                }

                /* ── hero ────────────────────────────────────────────── */
                .ab-hero {
                    position: relative;
                    display: grid;
                    grid-template-columns: minmax(0, 1.15fr) minmax(0, .85fr);
                    align-items: center;
                    gap: clamp(28px, 5vw, 72px);
                    max-width: 1240px;
                    margin: 0 auto;
                    padding: clamp(96px, 16vh, 190px) clamp(22px, 5vw, 56px) clamp(60px, 10vh, 120px);
                }
                .ab-hero-type { position: relative; }
                .ab-h1 {
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    gap: 10px;
                    margin: 18px 0 0;
                    line-height: .96;
                }
                .ab-cut {
                    display: inline-block;
                    font-size: clamp(34px, 7.4vw, 78px);
                    letter-spacing: -.02em;
                    transform-origin: 50% 50%;
                }
                /* written on the board, not cut from card */
                .ab-cut--pencil {
                    font-family: var(--font-geist-mono), ui-monospace, monospace;
                    font-size: clamp(13px, 2vw, 18px);
                    letter-spacing: .2em;
                    color: rgba(244,249,236,.82);
                    transform: rotate(-1.8deg);
                    margin-bottom: 2px;
                }
                /* cut from dark card, the way the mat cuts YOUR PHOTOS */
                .ab-cut--dark {
                    background: #241C15;
                    color: #F5EFE2;
                    padding: .12em .26em .2em;
                    transform: rotate(-1.6deg);
                    box-shadow: 3px 7px 0 rgba(20,16,10,.34);
                }
                .ab-cut--yellow {
                    background: #EFB61F;
                    color: #241C10;
                    padding: .1em .26em .18em;
                    margin-left: clamp(18px, 6vw, 92px);
                    transform: rotate(4.4deg);
                    box-shadow: 3px 7px 0 rgba(20,16,10,.3);
                }
                .ab-note {
                    display: inline-flex;
                    align-items: center;
                    gap: 10px;
                    margin: clamp(28px, 5vh, 46px) 0 0;
                    font-size: clamp(10px, 1.5vw, 12px);
                    letter-spacing: .16em;
                    text-transform: uppercase;
                    color: rgba(244,249,236,.78);
                    transform: rotate(1.2deg);
                }
                .ab-arrow { width: 74px; height: 28px; flex: 0 0 auto; }
                .ab-scissors {
                    position: absolute;
                    right: -6%;
                    bottom: -14%;
                    width: 66px;
                    /* the drawn props size themselves to their box, so the box
                     * has to carry the file's own ratio or it collapses */
                    aspect-ratio: 120 / 200;
                    opacity: .9;
                    transform: rotate(-24deg);
                    filter: drop-shadow(2px 6px 6px rgba(12,20,14,.5));
                    pointer-events: none;
                }

                /* the portrait, taped down */
                .ab-portrait {
                    position: relative;
                    margin: 0;
                    padding: 12px 12px 0;
                    background: #F6F1E6;
                    box-shadow: 4px 12px 0 rgba(14,24,18,.34);
                    transform: rotate(2.4deg);
                    transform-origin: 50% 50%;
                }
                .ab-portrait .ab-shot { aspect-ratio: 4 / 5; }
                .ab-portrait figcaption {
                    padding: 11px 2px 13px;
                    font-size: 9.5px;
                    letter-spacing: .14em;
                    text-transform: uppercase;
                    color: rgba(36,31,25,.55);
                }
                .ab-tape { position: absolute; width: 128px; height: 34px; z-index: 3; pointer-events: none;
                    filter: drop-shadow(1px 2px 2px rgba(20,16,10,.22)); }
                .ab-tape--tl { top: -16px; left: -26px; transform: rotate(-28deg); }
                .ab-tape--br { bottom: 10px; right: -30px; transform: rotate(-22deg); }
                .ab-tape--top { top: -17px; left: 50%; width: 150px; transform: translateX(-50%) rotate(-2.4deg); }

                /* ── statement ───────────────────────────────────────── */
                .ab-statement {
                    display: grid;
                    grid-template-columns: minmax(0, 1.35fr) minmax(0, .65fr);
                    align-items: start;
                    gap: clamp(26px, 4.5vw, 64px);
                    max-width: 1240px;
                    margin: 0 auto;
                    padding: clamp(40px, 8vh, 90px) clamp(22px, 5vw, 56px);
                }
                .ab-sheet {
                    position: relative;
                    background: #F6F1E6;
                    color: #241F19;
                    padding: clamp(26px, 4vw, 52px);
                    border-radius: 2px;
                    transform: rotate(-.55deg);
                    box-shadow: 6px 16px 0 rgba(14,24,18,.3), 0 2px 0 rgba(255,255,255,.5) inset;
                }
                .ab-clip {
                    position: absolute;
                    top: -26px;
                    right: clamp(28px, 8%, 70px);
                    width: 30px;
                    aspect-ratio: 46 / 110;
                    filter: drop-shadow(1px 3px 3px rgba(20,16,10,.35));
                }
                .ab-h2 {
                    margin: 0 0 18px;
                    font-size: clamp(23px, 3.5vw, 38px);
                    line-height: 1.05;
                    letter-spacing: -.015em;
                }
                .ab-h2--onmat { color: #F7FBF0; margin-top: 8px; }
                .ab-body {
                    margin: 0 0 15px;
                    font-size: clamp(14.5px, 1.35vw, 16.5px);
                    line-height: 1.66;
                    color: rgba(36,31,25,.84);
                    max-width: 62ch;
                }
                .ab-sign {
                    margin: 26px 0 0;
                    font-family: var(--font-archivo-black), Impact, sans-serif;
                    font-size: 20px;
                    transform: rotate(-2.4deg);
                    transform-origin: 0 50%;
                    color: #B23A2B;
                }
                .ab-side { margin: 0; padding: 11px 11px 0; background: #F6F1E6;
                    box-shadow: 4px 12px 0 rgba(14,24,18,.3); transform: rotate(1.4deg); }
                .ab-side .ab-shot { aspect-ratio: 3 / 4; }
                .ab-side figcaption {
                    padding: 10px 2px 12px;
                    font-size: 9.5px;
                    letter-spacing: .14em;
                    text-transform: uppercase;
                    color: rgba(36,31,25,.55);
                }

                /* ── numbers, printed straight on the board ──────────── */
                .ab-numbers {
                    display: grid;
                    grid-template-columns: repeat(4, minmax(0, 1fr));
                    gap: clamp(14px, 2.5vw, 34px);
                    max-width: 1240px;
                    margin: 0 auto;
                    padding: clamp(30px, 6vh, 62px) clamp(22px, 5vw, 56px);
                    border-top: 1px dashed rgba(238,244,222,.28);
                    border-bottom: 1px dashed rgba(238,244,222,.28);
                }
                .ab-num strong {
                    display: block;
                    font-size: clamp(30px, 5.4vw, 58px);
                    line-height: 1;
                    color: #F7FBF0;
                }
                .ab-num span {
                    display: block;
                    margin-top: 9px;
                    font-size: 9.5px;
                    letter-spacing: .14em;
                    text-transform: uppercase;
                    color: rgba(244,249,236,.6);
                }

                /* ── section heads ───────────────────────────────────── */
                .ab-sec-head { max-width: 1240px; margin: 0 auto clamp(22px, 4vh, 40px); }
                .ab-sheetwrap, .ab-steps, .ab-stocks, .ab-facts, .ab-cta {
                    max-width: 1240px;
                    margin: 0 auto;
                    padding: clamp(52px, 10vh, 118px) clamp(22px, 5vw, 56px);
                }
                .ab-sec-head .ab-h2 { max-width: 22ch; }

                /* ── the strip ───────────────────────────────────────── */
                .ab-strip {
                    position: relative;
                    background: #17140F;
                    padding: 26px 18px;
                    border-radius: 2px;
                    box-shadow: 6px 16px 0 rgba(10,18,13,.36);
                    transform: rotate(-.4deg);
                }
                .ab-sprockets {
                    position: absolute;
                    left: 0;
                    right: 0;
                    height: 13px;
                    background-image: repeating-linear-gradient(to right,
                        rgba(240,237,230,.92) 0 11px, transparent 11px 24px);
                    opacity: .85;
                }
                .ab-sprockets--top { top: 7px; }
                .ab-sprockets--bottom { bottom: 7px; }
                .ab-frames {
                    display: grid;
                    grid-template-columns: repeat(3, minmax(0, 1fr));
                    gap: 14px;
                }
                .ab-frame {
                    position: relative;
                    margin: 0;
                    background: #0F0D0B;
                    padding: 7px 7px 0;
                }
                .ab-frame.is-in {
                    transition: transform .28s cubic-bezier(.16,1,.3,1),
                                box-shadow .28s cubic-bezier(.16,1,.3,1);
                }
                .ab-frame .ab-shot { aspect-ratio: 3 / 2; }
                .ab-frame .ab-shot img { transition: filter .35s ease; }
                .ab-frame.is-in:hover { transform: translateY(-6px); box-shadow: 3px 12px 22px rgba(0,0,0,.45); }
                .ab-frame:hover .ab-shot img { filter: saturate(1.08) brightness(1.05); }
                .ab-frame figcaption {
                    display: grid;
                    grid-template-columns: auto 1fr;
                    gap: 2px 8px;
                    padding: 9px 2px 10px;
                    font-size: 9px;
                    letter-spacing: .1em;
                    text-transform: uppercase;
                    color: rgba(238,232,216,.62);
                }
                .ab-frame-n { grid-row: span 2; color: rgba(238,232,216,.35); }
                .ab-frame-t { color: rgba(246,241,230,.92); letter-spacing: .08em; }
                .ab-ring {
                    position: absolute;
                    left: -8px;
                    top: -8px;
                    width: calc(100% + 16px);
                    height: calc(100% + 16px);
                    overflow: visible;
                    pointer-events: none;
                    z-index: 4;
                }
                .ab-ring path { stroke-dasharray: 340; stroke-dashoffset: 340; }
                .ab-ring--frame { top: 0; height: calc(100% - 34px); }

                /* ── the four cards ──────────────────────────────────── */
                .ab-cards {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(238px, 1fr));
                    gap: clamp(18px, 2.4vw, 30px);
                    margin: 0;
                    padding: 0;
                    list-style: none;
                }
                .ab-card {
                    position: relative;
                    background: #F6F1E6;
                    color: #241F19;
                    padding: 26px 22px 24px;
                    border-radius: 2px;
                    transform: rotate(var(--rot, 0deg));
                    box-shadow: 4px 11px 0 rgba(14,24,18,.3);
                }
                .ab-card.is-in {
                    transition: transform .3s cubic-bezier(.16,1,.3,1),
                                box-shadow .3s cubic-bezier(.16,1,.3,1);
                }
                .ab-card.is-in:hover {
                    transform: rotate(var(--rot, 0deg)) translateY(-5px);
                    box-shadow: 6px 16px 0 rgba(14,24,18,.34);
                }
                .ab-card-n {
                    position: relative;
                    display: inline-block;
                    margin-bottom: 16px;
                    padding: 2px 7px;
                    font-family: var(--font-geist-mono), ui-monospace, monospace;
                    font-size: 12px;
                    letter-spacing: .1em;
                    color: #B23A2B;
                }
                .ab-ring--num { left: -6px; top: -5px; width: calc(100% + 12px); height: calc(100% + 10px); }
                .ab-card h3 { margin: 0 0 9px; font-size: 19px; letter-spacing: -.005em; }
                .ab-card p { margin: 0; font-size: 14px; line-height: 1.62; color: rgba(36,31,25,.78); }

                /* ── the stocks ──────────────────────────────────────── */
                .ab-stocklist { margin: 0; padding: 0; list-style: none; }
                .ab-stock {
                    display: grid;
                    grid-template-columns: 148px 190px minmax(0, 1fr);
                    align-items: center;
                    gap: 20px;
                    padding: 17px 4px;
                    border-top: 1px dashed rgba(238,244,222,.26);
                }
                .ab-stock:last-child { border-bottom: 1px dashed rgba(238,244,222,.26); }
                .ab-chip {
                    display: inline-block;
                    justify-self: start;
                    padding: 7px 13px 6px;
                    border-radius: 2px;
                    background: var(--paper);
                    color: var(--ink);
                    font-size: 13px;
                    letter-spacing: .02em;
                    box-shadow: 2px 4px 0 rgba(10,18,13,.32);
                    transform: rotate(-1.4deg);
                    transition: transform .22s cubic-bezier(.16,1,.3,1);
                }
                .ab-stock:hover .ab-chip { transform: rotate(-1.4deg) translateY(-3px); }
                .ab-stock-film {
                    font-size: 10.5px;
                    letter-spacing: .14em;
                    text-transform: uppercase;
                    color: rgba(244,249,236,.62);
                }
                .ab-stock-note { font-size: 14.5px; line-height: 1.55; color: rgba(247,251,240,.9); }
                .ab-stocks-foot { margin: 30px 0 0; font-size: 11px; letter-spacing: .14em; text-transform: uppercase; }
                .ab-stocks-foot a {
                    color: #EFB61F;
                    text-decoration: none;
                    border-bottom: 1px solid rgba(239,182,31,.4);
                    padding-bottom: 3px;
                    transition: border-color .2s ease, color .2s ease;
                }
                .ab-stocks-foot a:hover { color: #FFD35E; border-color: #FFD35E; }

                /* ── pinned slips ────────────────────────────────────── */
                .ab-notes {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(232px, 1fr));
                    gap: clamp(20px, 2.6vw, 32px);
                    margin: 0;
                    padding: 0;
                    list-style: none;
                }
                .ab-noteslip {
                    position: relative;
                    background: #EFE7D6;
                    color: #241F19;
                    padding: 30px 20px 22px;
                    transform: rotate(var(--rot, 0deg));
                    box-shadow: 3px 9px 0 rgba(14,24,18,.28);
                }
                .ab-noteslip.is-in { transition: transform .3s cubic-bezier(.16,1,.3,1); }
                .ab-noteslip.is-in:hover { transform: rotate(var(--rot, 0deg)) translateY(-4px); }
                .ab-pin {
                    position: absolute;
                    top: -11px;
                    left: 50%;
                    width: 24px;
                    height: 24px;
                    transform: translateX(-50%);
                    filter: drop-shadow(1px 3px 3px rgba(20,16,10,.4));
                }
                .ab-noteslip strong {
                    display: block;
                    font-size: 9.5px;
                    font-weight: 500;
                    letter-spacing: .16em;
                    text-transform: uppercase;
                    color: rgba(36,31,25,.5);
                    margin-bottom: 6px;
                }
                .ab-noteslip span { font-size: 14.5px; line-height: 1.5; }

                /* ── the last sheet ──────────────────────────────────── */
                .ab-ctasheet {
                    position: relative;
                    max-width: 720px;
                    margin: 0 auto;
                    background: #F6F1E6;
                    color: #241F19;
                    padding: clamp(34px, 5vw, 56px);
                    border-radius: 2px;
                    text-align: center;
                    transform: rotate(.5deg);
                    box-shadow: 6px 18px 0 rgba(14,24,18,.32);
                }
                .ab-ctasheet .ab-h2 { margin-bottom: 14px; }
                .ab-ctasheet .ab-body { margin: 0 auto 26px; max-width: 46ch; }
                .ab-ctarow {
                    display: flex;
                    flex-wrap: wrap;
                    align-items: center;
                    justify-content: center;
                    gap: 16px 26px;
                }
                .ab-btn {
                    display: inline-block;
                    padding: 13px 22px 12px;
                    border-radius: 2px;
                    background: #241F19;
                    color: #F6F1E6;
                    font-size: 12px;
                    letter-spacing: .08em;
                    text-transform: uppercase;
                    text-decoration: none;
                    box-shadow: 2px 5px 0 rgba(20,16,10,.3);
                    transition: transform .16s ease, box-shadow .16s ease;
                }
                .ab-btn:hover { transform: translateY(-2px); box-shadow: 3px 8px 0 rgba(20,16,10,.34); }
                .ab-btn:active { transform: none; box-shadow: 1px 2px 0 rgba(20,16,10,.34); }
                .ab-mail {
                    font-size: 12px;
                    letter-spacing: .06em;
                    color: rgba(36,31,25,.72);
                    text-decoration: none;
                    border-bottom: 1px solid rgba(36,31,25,.3);
                    padding-bottom: 2px;
                    transition: color .18s ease, border-color .18s ease;
                }
                .ab-mail:hover { color: #241F19; border-color: #241F19; }

                .ab-foot {
                    display: flex;
                    flex-wrap: wrap;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                    max-width: 1240px;
                    margin: 0 auto;
                    padding: 40px clamp(22px, 5vw, 56px) 54px;
                    font-size: 10px;
                    letter-spacing: .14em;
                    text-transform: uppercase;
                    color: rgba(244,249,236,.5);
                    border-top: 1px dashed rgba(238,244,222,.22);
                }
                .ab-foot p { margin: 0; }
                .ab-foot a { color: rgba(244,249,236,.72); text-decoration: none; }
                .ab-foot a:hover { color: #FFFCF4; }

                :where(.ab-root) a:focus-visible,
                :where(.ab-root) button:focus-visible {
                    outline: 2px dashed #F2E9CF;
                    outline-offset: 5px;
                }

                /* ── the board at other sizes ────────────────────────── */
                @media (max-width: 1000px) {
                    .ab-hero { grid-template-columns: 1fr; gap: 44px; padding-top: clamp(96px, 14vh, 150px); }
                    .ab-portrait { max-width: 460px; }
                    .ab-statement { grid-template-columns: 1fr; }
                    .ab-side { max-width: 380px; }
                    .ab-frames { grid-template-columns: repeat(2, minmax(0, 1fr)); }
                    .ab-stock { grid-template-columns: 128px minmax(0, 1fr); gap: 10px 18px; }
                    .ab-stock-note { grid-column: 1 / -1; }
                }
                @media (max-width: 640px) {
                    .ab-root { --minor: 22px; --major: 110px; }
                    .ab-numbers { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 22px 14px; }
                    .ab-frames { grid-template-columns: 1fr; }
                    .ab-cut--yellow { margin-left: 0; }
                    .ab-scissors { display: none; }
                    .ab-tape--br { right: -18px; }
                    .ab-foot { justify-content: flex-start; }
                }
                @media (prefers-reduced-motion: reduce) {
                    /* opacity only — the rotations below are the DESIGN, not
                     * the animation, and a blanket transform reset here would
                     * square every sheet on the desk */
                    [data-reveal], [data-hero] { opacity: 1; }
                    .ab-veil { display: none; }
                    .ab-shot img { transform: none; }
                    .ab-ring path { stroke-dashoffset: 0; }
                    .ab-frame, .ab-card, .ab-noteslip, .ab-chip, .ab-btn { transition: none; }
                }
            `}</style>
        </div>
    )
}
